import React, { useState, useMemo } from "react";
import DataTable from "components/DataTable";
import PageWrapper from "components/PageWrapper";
import { useTableData } from "../../hooks/useTableData";
import { walletSettlementReportsConfig } from "config/tableConfigs";
import { useSession } from "hooks/useSession";
import PayoutReceiptModal from "./PayoutReceipt";
import { exportToCSV, exportToExcel } from "../../utils/exportUtils";

const WalletSettlementReportsPage = () => {
  const { userid, role } = useSession();
  const userRole = role === "Platform User" ? "Admin" : role;
  const [loading] = useState(false);
const [selectedRow, setSelectedRow] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const getLast7Days = () => {
    const today = new Date();
    const last7 = new Date();
    last7.setDate(today.getDate() - 1)  ;

    return {
      start: last7.toISOString().split("T")[0],
      end: today.toISOString().split("T")[0],
    };
  };

 const getTodayRange = () => {
  const today = new Date();
  const todayStr = today.toISOString().split("T")[0];
  return {
    start: todayStr,
    end: todayStr,
  };
};

const { start, end } = getTodayRange();

  const [apiUrl, setApiUrl] = useState(
    `https://pro.mokshiq.in/get_payout_report?user=${userid}&filter_date=${start}&end_date=${end}`
  );

  // Keep the currently selected date range around for export filenames
  // and for a friendlier "no rows to export" message.
  const [dateRange, setDateRange] = useState({ start, end });

  const tableData = useTableData(apiUrl, 0, walletSettlementReportsConfig);

  const handleDateFilter = (s, e) => {
    setApiUrl(
      `https://pro.mokshiq.in/get_payout_report?user=${userid}&filter_date=${s}&end_date=${e}`
    );
    setDateRange({ start: s, end: e });
  };

  // 🔥 ROLE → COLUMN BINDING (SAME AS DMT)
  const filteredConfig = useMemo(() => {
    const base = { ...walletSettlementReportsConfig };

   const roleMap = {
  Retailer: [
    "id",
    "ref_no",
    "ack_no",
    "product",
    "account_number",
    "holder_name",
    "number",
    "amount",
    "created_at",
    "status",
    "Opening Balance",
    "Commission",
    "TDS",
    "Charge",
    "GST",
    "Closing Balance",
    "bank_rrn",
  ],

  Distributer: [
    "id",
    "ref_no",
    "ack_no",
    "product",
    "account_number",
    "holder_name",
    "number",
    "amount",
    "created_at",
    "status",
    "openingBalanceDistributor",
    "commission",
    "tds",
    "charge",
    "gst",
    "closingBalanceDistributor",
    "bank_rrn",
  ],

  "Super Distributer": [
    "id",
    "ref_no",
    "ack_no",
    "product",
    "account_number",
    "holder_name",
    "number",
    "amount",
    "created_at",
    "status",
    "openingBalanceSuperDistributor",
    "commission",
    "tds",
    "charge",
    "gst",
    "closingBalanceSuperDistributor",
    "bank_rrn",
  ],
  "White label": [
    "id",
    "bcName",          // Agent Name from your JSON
    "ref_no",
    "ack_no",
    "account_number",  // From your JSON
    "holder_name",     // From your JSON
    "amount",
    "status",

    // White Label's Own Financials
    "Opening Balance",
    "Commission",
    "TDS",
    "Charge",
    "GST",
    "Closing Balance",

    // Agent's (BC) Financials
    "Opening Balance BC",
    "BC Commission",
    "BC TDS",
    "BC Charge",
    "Closing Balance BC",

    "bank_rrn",
    "created_at"
  ],

  Admin: [
    "id",
    "ref_no",
    "ack_no",
    "product",
    "number",
    "amount",
    "created_at",
    "status",
    "bcName",
    "openingBalanceAdmin",
    "commissionAdmin",
    "tdsAdmin",
    "chargeAdmin",
    "gstAdmin",
    "closingBalanceAdmin",
    "walletTxn",
    "txnFlow",
    "bank_rrn",
  ],

  "Main Admin": walletSettlementReportsConfig.columns.map((c) => c.key),
};


    const allowedKeys = roleMap[userRole] || [];
    base.columns = base.columns.filter((c) =>
      allowedKeys.includes(c.key)
    );

    return base;
  }, [userRole]);

    const handleView = (row) => {
    setSelectedRow(row);
    setShowReceipt(true);
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setSelectedRow(null);
  };

  // Export uses filteredConfig.columns — the SAME role-filtered column list
  // DataTable renders — so a CSV/Excel export can never contain a column
  // this role isn't shown on screen, even if the raw API row includes it
  // (e.g. Retailer never gets Admin's commissionAdmin/gstAdmin fields).
  const exportFilenameBase = `wallet_settlement_${userRole.replace(/\s+/g, "_")}_${dateRange.start}_to_${dateRange.end}`;

  const handleExportCSV = () => {
    const { ok } = exportToCSV(tableData, filteredConfig.columns, exportFilenameBase);
    if (!ok) {
      window.alert("No rows to export for the selected date range.");
    }
  };

  const handleExportExcel = () => {
    const { ok } = exportToExcel(tableData, filteredConfig.columns, exportFilenameBase);
    if (!ok) {
      window.alert("No rows to export for the selected date range.");
    }
  };

  return (
    <div>
      {loading && (
        <div className="page-loader-overlay d-flex justify-content-center align-items-center">
          <div className="spinner-border text-primary" />
        </div>
      )}

      <PageWrapper
        config={walletSettlementReportsConfig}
        onDateFilter={handleDateFilter}

      >
        <div className="d-flex justify-content-end gap-2 mb-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleExportCSV}
          >
            Export CSV
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            onClick={handleExportExcel}
          >
            Export Excel
          </button>
        </div>

        <DataTable
          config={filteredConfig}
          tableData={tableData}
          onView={handleView}
          onDelete={() => {}}
          onEdit={() => {}}
          onExport={handleExportExcel}
          onDeleteSelected={() => {}}
        />
      </PageWrapper>
      <PayoutReceiptModal
  isOpen={showReceipt}
  onClose={() => setShowReceipt(false)}
  payoutId={selectedRow?.id}   // or receiptData={selectedRow} if the row already has full data
/>
    </div>
  );
};

export default WalletSettlementReportsPage;